/**
 * Client-side graph filtering that mirrors the table's column filters.
 *
 * The Data Explorer table filters rows with PrimeReact's `DataTable`
 * (client-side) in every non-schema mode. To keep the graph view in sync with
 * the table, we evaluate the *same* `DataTableFilterMeta` against the in-memory
 * graph nodes here, dropping nodes that don't match and pruning any
 * relationships left dangling.
 *
 * Filters are scoped per node label (one filter set per table tab): a node only
 * has to satisfy the constraints registered for *its own* display label. Nodes
 * of other labels pass through untouched unless they have their own active
 * filter — this matches how the per-tab table filters behave.
 *
 * Match-mode semantics intentionally mirror PrimeReact's `FilterService` (all
 * string comparisons are case-insensitive) so the graph and the table always
 * agree on which entities survive a filter.
 */
import type { DataTableFilterMeta } from 'primereact/datatable';
import { flattenFilterMetaToConstraints } from '@/helpers/pipelineBuilders.helpers';
import {
  getDisplayNodeLabel,
  type FilterConstraint,
} from '@/helpers/graph.helpers';
import { getGraphBusinessId } from '@/helpers/graphIdentity.helpers';
import type { GraphData, GraphNode, GraphRelationship } from '@/types/graph.types';

export type GraphFiltersByLabel = Record<string, DataTableFilterMeta>;

function readNodeFieldValue(node: GraphNode, field: string): unknown {
  const fromProperties = node.properties?.[field];
  if (fromProperties !== undefined) {
    return fromProperties;
  }
  return (node as Record<string, unknown>)[field];
}

function toComparable(value: unknown): { num: number | null; str: string } {
  if (value === undefined || value === null) {
    return { num: null, str: '' };
  }
  const str = String(value);
  const num = Number(str);
  return { num: str.trim() !== '' && Number.isFinite(num) ? num : null, str };
}

/**
 * Evaluates a single PrimeReact-style constraint against a node field value.
 * Empty filter values always pass (consistent with the table and the pipeline
 * predicate builder, which skip blank constraints).
 */
export function evaluateConstraint(
  fieldValue: unknown,
  matchMode: string,
  filterValue: string,
): boolean {
  if (filterValue == null || String(filterValue).trim() === '') {
    return true;
  }
  const mode = (matchMode || 'contains').toLowerCase();
  const field = toComparable(fieldValue);
  const filter = toComparable(filterValue);
  const fieldStr = field.str.toLowerCase();
  const filterStr = filter.str.toLowerCase();

  switch (mode) {
    case 'contains':
      return fieldStr.includes(filterStr);
    case 'notcontains':
      return !fieldStr.includes(filterStr);
    case 'equals':
      return fieldStr === filterStr;
    case 'notequals':
      return fieldStr !== filterStr;
    case 'startswith':
      return fieldStr.startsWith(filterStr);
    case 'endswith':
      return fieldStr.endsWith(filterStr);
    case 'lt':
    case 'lte':
    case 'gt':
    case 'gte': {
      if (fieldValue === undefined || fieldValue === null || field.str === '') {
        return false;
      }
      const useNumeric = field.num !== null && filter.num !== null;
      const cmp = useNumeric
        ? (field.num as number) - (filter.num as number)
        : field.str.localeCompare(filter.str);
      if (mode === 'lt') return cmp < 0;
      if (mode === 'lte') return cmp <= 0;
      if (mode === 'gt') return cmp > 0;
      return cmp >= 0;
    }
    default:
      // Fail closed: an unknown operator must not let extra nodes through the
      // graph that the table would have filtered out.
      return false;
  }
}

function nodeMatchesConstraints(
  node: GraphNode,
  constraints: FilterConstraint[],
): boolean {
  return constraints.every((c) =>
    evaluateConstraint(readNodeFieldValue(node, c.field), c.matchMode, c.value),
  );
}

/**
 * True when a node satisfies the active filters for its display label. Nodes
 * whose label has no active filter always pass.
 */
export function nodeMatchesFilters(
  node: GraphNode,
  filtersByLabel: GraphFiltersByLabel | undefined,
): boolean {
  if (!filtersByLabel) return true;
  const label = getDisplayNodeLabel(node.labels);
  const meta = filtersByLabel[label];
  if (!meta) return true;
  const constraints = flattenFilterMetaToConstraints(meta).filter(
    (c) => c.value != null && String(c.value).trim() !== '',
  );
  if (constraints.length === 0) return true;
  return nodeMatchesConstraints(node, constraints);
}

/**
 * Filters already-flattened table rows against a single label's
 * `DataTableFilterMeta`, mirroring {@link nodeMatchesFilters} but reading
 * fields straight off the row (rows spread node properties as top-level keys,
 * see `graphNodesToTableRows`). Used to make the "Send to dashboard" preview
 * match the on-screen filtered table; the rendered card applies the same
 * constraints at the graph-node level, so both agree on which rows survive.
 * Returns the input unchanged (same reference) when there are no constraints.
 */
export function filterTableRowsByMeta<T extends Record<string, unknown>>(
  rows: T[],
  meta: DataTableFilterMeta | undefined,
): T[] {
  if (!meta) return rows;
  const constraints = flattenFilterMetaToConstraints(meta).filter(
    (c) => c.value != null && String(c.value).trim() !== '',
  );
  if (constraints.length === 0) return rows;
  return rows.filter((row) =>
    constraints.every((c) =>
      evaluateConstraint(row[c.field], c.matchMode, c.value),
    ),
  );
}

/** True when at least one label has a non-empty filter constraint. */
export function hasActiveGraphFilters(
  filtersByLabel: GraphFiltersByLabel | undefined,
): boolean {
  if (!filtersByLabel) return false;
  return Object.values(filtersByLabel).some((meta) =>
    flattenFilterMetaToConstraints(meta).some(
      (c) => c.value != null && String(c.value).trim() !== '',
    ),
  );
}

/**
 * Computes the DataTable page for a selected row when client-side filters may
 * shrink the visible list. Using the unfiltered index while filters are active
 * can set `first` past the filtered length (empty table).
 *
 * @param unfilteredIndex - Row index in the full (unfiltered) tab data, or -1
 * @param filteredIndex - Row index in the filtered list, or -1 if not present
 * @param rowsPerPage - Page size (must be > 0)
 * @param hasClientFilters - Whether instance-view client filters are active
 * @returns Zero-based page index for the controlled DataTable `first` prop
 */
export function resolveSelectTargetPage({
  unfilteredIndex,
  filteredIndex,
  rowsPerPage,
  hasClientFilters,
}: {
  unfilteredIndex: number;
  filteredIndex: number;
  rowsPerPage: number;
  hasClientFilters: boolean;
}): number {
  if (rowsPerPage <= 0) return 0;
  if (hasClientFilters) {
    // Selection not in the filtered set: stay on page 0 so the filtered view
    // remains visible instead of jumping past the end of the filtered list.
    if (filteredIndex < 0) return 0;
    return Math.floor(filteredIndex / rowsPerPage);
  }
  if (unfilteredIndex < 0) return 0;
  return Math.floor(unfilteredIndex / rowsPerPage);
}

/**
 * Filters a graph to the nodes that match the table's per-label filters and
 * prunes relationships whose endpoints were removed. Returns the input
 * unchanged (same reference) when there are no active filters.
 */
export function filterGraphByFilters(
  nodes: GraphNode[],
  relationships: GraphRelationship[],
  filtersByLabel: GraphFiltersByLabel | undefined,
): GraphData {
  if (!hasActiveGraphFilters(filtersByLabel)) {
    return { nodes, relationships };
  }
  const keptNodes = nodes.filter((node) =>
    nodeMatchesFilters(node, filtersByLabel),
  );
  if (keptNodes.length === nodes.length) {
    return { nodes, relationships };
  }
  const activeIds = new Set(
    keptNodes.flatMap((node) => {
      const ids = [String(node.id)];
      const businessId = getGraphBusinessId(node);
      return businessId ? [...ids, String(businessId)] : ids;
    }),
  );
  const keptRelationships = relationships.filter(
    (rel) => activeIds.has(String(rel.from)) && activeIds.has(String(rel.to)),
  );
  return { nodes: keptNodes, relationships: keptRelationships };
}
